/**
 * Chinese Converter Page — Route Module
 *
 * Traditional ↔ Simplified Chinese character conversion tool.
 */
import { zhToSimplified, zhToTraditional } from '@letsgethome/lib/wasm/web-only';
import { plaintextString } from '@letsgethome/types/branded';
import { Button } from '@letsgethome/ui/button';
import { Label } from '@letsgethome/ui/label';
import { RadioGroup, RadioGroupItem } from '@letsgethome/ui/radio-group';
import { Textarea } from '@letsgethome/ui/textarea';
import { UnstyledButton } from '@letsgethome/ui/unstyled-button';
import { useState } from 'react';

import { SeoHead } from '../../../../shared/components/SeoHead';
import { useStaticSeo } from '../../../../shared/hooks/seo/use-static-seo';
import { useTranslations } from '../../../../shared/i18n/useTranslations';

// The trad↔simp character table lives in the Rust SSOT
// (domain_core::zh_convert) and runs here as WebAssembly. The forward
// (Traditional → Simplified) table is generated from OpenCC's per-character
// default (first candidate). The reverse stays on the legacy 354 keys, with
// last-write-wins on a shared simplified form (#7693).
const convertToSimplified = zhToSimplified;
const convertToTraditional = zhToTraditional;

/**
 * LET-134 — the direction radio group is named by the heading ABOVE it, which is
 * already on the glass and already says what the choice is about ("Conversion
 * between Traditional Chinese and Simplified Chinese").
 *
 * No `useLabelledGroup()` here, deliberately: that hook mints a NEW label
 * element, and this group's label already exists as the section's `<h2>`. A
 * minted one would be a second visible heading, or a `sr-only` duplicate of the
 * first — both worse than pointing at the real one. A module const rather than a
 * `useId`, because this page renders exactly one converter and the id has to be
 * reachable from a heading and a group that are siblings, not a bag.
 */
const CONVERTER_HEADING_ID = 'chinese-converter-heading';

export function Component() {
  const t = useTranslations('tools.chineseConverter');

  const [direction, setDirection] = useState<'toSimplified' | 'toTraditional'>('toSimplified');
  const [sourceText, setSourceText] = useState('');
  const [outputText, setOutputText] = useState('');
  const [copied, setCopied] = useState(false);

  const handleConvert = () => {
    setOutputText(
      direction === 'toSimplified'
        ? convertToSimplified(sourceText)
        : convertToTraditional(sourceText)
    );
  };

  const handleCopy = async () => {
    if (outputText) {
      await navigator.clipboard.writeText(outputText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleToggle = () => {
    setDirection((prev) => (prev === 'toSimplified' ? 'toTraditional' : 'toSimplified'));
    const temp = sourceText;
    setSourceText(outputText);
    setOutputText(temp);
  };

  const inputLabel = t('converter.fieldLabel', {
    field: direction === 'toSimplified' ? t('converter.traditional') : t('converter.simplified'),
    qualifier: t('converter.input'),
  });

  const outputLabel = t('converter.fieldLabel', {
    field: direction === 'toSimplified' ? t('converter.simplified') : t('converter.traditional'),
    qualifier: t('converter.output'),
  });

  return (
    <>
      <SeoHead
        {...useStaticSeo('/tools/chinese-converter', {
          title: t('seo.title'),
          description: t('seo.description'),
        })}
      />
      <div className="min-h-screen bg-gradient-to-b from-muted to-background">
        {/* Hero */}
        <section className="bg-gradient-to-r from-primary to-primary/90 py-16 text-primary-foreground">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-3xl text-center">
              <div className="mb-6 text-5xl">&#x6587;</div>
              <h1 className="mb-4 text-4xl font-bold md:text-5xl">{t('pageTitle')}</h1>
              <p className="text-xl text-primary-foreground/70">{t('subtitle')}</p>
            </div>
          </div>
        </section>

        {/* Converter */}
        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-4xl rounded-2xl bg-card p-8 shadow-xl">
              <h2
                id={CONVERTER_HEADING_ID}
                className="mb-6 text-center text-2xl font-bold text-foreground"
              >
                {t('converter.title')}
              </h2>

              <div className="mb-6">
                <RadioGroup
                  aria-labelledby={CONVERTER_HEADING_ID}
                  value={direction}
                  onValueChange={(v: string) => setDirection(v as 'toSimplified' | 'toTraditional')}
                  className="flex flex-col justify-center gap-4 sm:flex-row"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="toSimplified" id="toSimplified" />
                    <Label htmlFor="toSimplified" className="cursor-pointer font-medium">
                      {t('converter.toSimplified')}
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="toTraditional" id="toTraditional" />
                    <Label htmlFor="toTraditional" className="cursor-pointer font-medium">
                      {t('converter.toTraditional')}
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <Label htmlFor="sourceText" className="mb-2 block text-foreground">
                    {inputLabel}
                  </Label>
                  <Textarea
                    id="sourceText"
                    value={sourceText}
                    onChange={(e) => setSourceText(e.target.value.slice(0, 200))}
                    className="min-h-[160px] resize-none"
                    placeholder={t('converter.inputPlaceholder')}
                  />
                  <p className="mt-1 text-sm text-muted-foreground">{sourceText.length}/200</p>
                </div>

                <div>
                  <Label className="mb-2 block text-foreground">{outputLabel}</Label>
                  <div className="relative">
                    <div className="min-h-[160px] whitespace-pre-wrap rounded-md border border-border bg-muted p-3 text-foreground">
                      {outputText ? (
                        plaintextString(outputText)
                      ) : (
                        <span className="text-muted-foreground">
                          {t('converter.outputPlaceholder')}
                        </span>
                      )}
                    </div>
                    {outputText && (
                      <UnstyledButton
                        onClick={() => void handleCopy()}
                        className="absolute right-2 top-2 rounded-md bg-card p-2 shadow transition-colors hover:bg-muted"
                        title={t('converter.copy')}
                      >
                        {copied ? '\u2713' : '\u2398'}
                      </UnstyledButton>
                    )}
                  </div>
                </div>
              </div>

              <div className="my-6 flex justify-center">
                <Button variant="outline" onClick={handleToggle} className="rounded-full px-6">
                  &#x21C4;{' '}
                  {direction === 'toSimplified'
                    ? t('converter.toTraditional')
                    : t('converter.toSimplified')}
                </Button>
              </div>

              <div className="flex justify-center">
                <Button onClick={handleConvert} size="lg" className="px-8 py-6 text-lg">
                  {t('converter.submitButton')}
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* How to Use */}
        <section className="bg-muted py-12">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-3xl">
              <h2 className="mb-6 text-2xl font-bold text-foreground">{t('howToUse.title')}</h2>
              <div className="space-y-4">
                {[
                  t('howToUse.step1'),
                  t('howToUse.step2'),
                  t('howToUse.step3'),
                  t('howToUse.step4'),
                ].map((step, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground">
                      {i + 1}
                    </div>
                    <p className="pt-1 text-foreground">{step}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Advantages */}
        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-3xl">
              <h2 className="mb-6 text-2xl font-bold text-foreground">{t('advantages.title')}</h2>
              <div className="grid gap-4 md:grid-cols-2">
                {[
                  t('advantages.point1'),
                  t('advantages.point2'),
                  t('advantages.point3'),
                  t('advantages.point4'),
                ].map((point, i) => (
                  <div key={i} className="rounded-lg border border-border bg-card p-4 shadow">
                    <p className="text-foreground">{point}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-muted py-12">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-3xl">
              <h2 className="mb-4 text-2xl font-bold text-foreground">{t('callToAction')}</h2>
              <div className="space-y-4 text-foreground">
                <p>{t('footerNote.p1')}</p>
                <p>{t('footerNote.p2')}</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
